import '../../styles/restScreens.css';
import { useEffect, useRef, useState } from 'react';
import { decodePolyline, vehicleGlyph } from '../../lib/liveTracking';

/**
 * LiveTripMap — BL-187 Gate 3's map render, Gate 4's tracking dot.
 *
 * Ground truth: IntelAgent_BL187_GPS_LiveTracking_Build_v1_01.md.
 *
 * Mapbox GL JS is imported LAZILY, inside the effect, on purpose. It is a large
 * library and every module that loads it pays for it in the main bundle; almost
 * nobody in this app opens a live trip map, so it should not be in the bundle
 * everyone downloads to see a dashboard. The lazy import also keeps GL JS out of
 * jest entirely — it needs a WebGL context that jsdom does not have — so the
 * component stays testable without mocking the whole SDK.
 *
 * Billing note: this is the Maps SDK layer, billed per map load and never
 * counting toward the Navigation SDK's metered MAU. No trip session is started
 * here. See server/mapboxRouting.js for the full reasoning.
 */

// Mapbox's own neutral base. White-labelling rides on top via the accent colour
// pulled from the live brand variables, so a tenant's palette moves the dot and
// route line without a second map style to maintain.
const MAP_STYLE = 'mapbox://styles/mapbox/streets-v12';

function brandAccent(fallback = '#03B7C9') {
    if (typeof window === 'undefined' || !window.getComputedStyle) return fallback;
    try {
        const raw = getComputedStyle(document.documentElement).getPropertyValue('--brand-500').trim();
        // global.css stores these as "R G B" channels so Tailwind opacity
        // modifiers work, so they need reassembling into a colour GL JS accepts.
        if (/^\d+\s+\d+\s+\d+$/.test(raw)) return `rgb(${raw.split(/\s+/).join(', ')})`;
        return raw || fallback;
    } catch {
        return fallback;
    }
}

function LiveTripMap({ position, routeGeometry = null, vehicleType = null, stale = false, className = '' }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const [loadError, setLoadError] = useState('');
    const [ready, setReady] = useState(false);

    const token = process.env.REACT_APP_MAPBOX_TOKEN;

    // ── Create the map once ──────────────────────────────────────────────────
    useEffect(() => {
        if (!token || !containerRef.current || mapRef.current) return undefined;

        let cancelled = false;
        let map = null;

        (async () => {
            try {
                const mapboxgl = (await import('mapbox-gl')).default;
                await import('mapbox-gl/dist/mapbox-gl.css');
                if (cancelled || !containerRef.current) return;

                mapboxgl.accessToken = token;
                map = new mapboxgl.Map({
                    container: containerRef.current,
                    style: MAP_STYLE,
                    center: position ? [position.longitude, position.latitude] : [-84.388, 33.749],
                    zoom: position ? 13 : 9,
                    // The client is watching a dot, not surveying terrain. Removing
                    // rotate/pitch keeps a one-handed phone drag from tilting the
                    // map into an unrecoverable angle.
                    pitchWithRotate: false,
                    dragRotate: false,
                    attributionControl: true,
                });
                map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

                const el = document.createElement('div');
                el.className = 'intelagent-tracking-dot';
                el.textContent = vehicleGlyph(vehicleType);
                el.style.setProperty('--dot-accent', brandAccent());

                markerRef.current = new mapboxgl.Marker({ element: el }).setLngLat(
                    position ? [position.longitude, position.latitude] : [-84.388, 33.749]
                );
                if (position) markerRef.current.addTo(map);

                map.on('load', () => {
                    if (!cancelled) setReady(true);
                });
                mapRef.current = map;
            } catch (error) {
                if (!cancelled) {
                    setLoadError('The map could not be loaded.');
                }
            }
        })();

        return () => {
            cancelled = true;
            try {
                markerRef.current?.remove();
                mapRef.current?.remove();
            } catch {
                // Already torn down.
            }
            markerRef.current = null;
            mapRef.current = null;
        };
        // Created once. Position changes move the marker in the effect below
        // rather than rebuilding the map, which would flash and refetch tiles —
        // and, since Maps SDK is billed per map load, cost money each time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // ── Move the dot as pings arrive ─────────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        const marker = markerRef.current;
        if (!map || !marker || !position) return;

        marker.setLngLat([position.longitude, position.latitude]);
        if (!marker._map) marker.addTo(map);

        const el = marker.getElement();
        if (el) {
            el.classList.toggle('is-stale', Boolean(stale));
            // Point the glyph the way the car is travelling when the device
            // reported a heading. Absent heading leaves it upright rather than
            // snapping to north, which would read as a real direction.
            if (Number.isFinite(position.heading)) {
                el.style.setProperty('--dot-heading', `${position.heading}deg`);
            }
        }

        map.easeTo({ center: [position.longitude, position.latitude], duration: 800 });
    }, [position, stale]);

    // ── Draw / update the route line ─────────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !ready) return;

        const coordinates = decodePolyline(routeGeometry);
        const data = {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates },
        };

        try {
            const existing = map.getSource('trip-route');
            if (existing) {
                existing.setData(data);
                return;
            }
            if (!coordinates.length) return;

            map.addSource('trip-route', { type: 'geojson', data });
            map.addLayer({
                id: 'trip-route-line',
                type: 'line',
                source: 'trip-route',
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: {
                    'line-color': brandAccent(),
                    'line-width': 4,
                    'line-opacity': 0.75,
                },
            }, /* insert beneath labels where possible */ undefined);
        } catch {
            // A style still settling can reject addSource/addLayer. The dot is the
            // load-bearing part; the route line is decoration and is retried on
            // the next position update.
        }
    }, [routeGeometry, ready]);

    if (!token) {
        // A missing credential is a configuration problem. Saying so plainly beats
        // an empty grey box that reads as "the driver has vanished".
        return (
            <div data-m6-layout="tracking" data-m6-component="LiveTripMap" className={`rounded-xl border border-[var(--surface-border)] bg-[var(--surface-sunken)] p-6 text-center ${className}`}>
                <p className="text-sm text-[var(--ink-muted)]">
                    Live map is not configured for this environment.
                </p>
            </div>
        );
    }

    return (
        <div data-m6-layout="tracking" data-m6-component="LiveTripMap" className={`relative overflow-hidden rounded-xl border border-[var(--surface-border)] ${className}`}>
            <div ref={containerRef} className="h-64 w-full md:h-80" data-testid="live-trip-map" />
            {loadError && (
                <div className="absolute inset-0 flex items-center justify-center bg-[var(--surface-sunken)] p-4 text-center">
                    <p className="text-sm text-[var(--ink-muted)]">{loadError}</p>
                </div>
            )}
        </div>
    );
}

export default LiveTripMap;
